import { Routes } from '@angular/router';
import { RoutePath } from './enums/api.enum';
import { MsalGuard } from '@azure/msal-angular';
import { UiLayoutComponent } from './ui-layout/ui-layout.component';
import { AuthComponent } from './pages/auth/auth.component';
import { AiChatComponent } from './pages/ai-chat/ai-chat.component';
import { apiGuard, authGuard } from './guards/auth.guard';
import { AgentManagerComponent } from './pages/agent/agent-manager/agent-manager.component';
import { AgentBuilderComponent } from './pages/agent/my-agents/agent-builder/agent-builder.component';
import { AgentListComponent } from './pages/agent/my-agents/agent-list/agent-list.component';
import { GsmManagerComponent } from "./pages/admin/gsm-manager/gsm-manager.component";
import { ManageSessionsComponent } from "./pages/manage-sessions/manage-sessions.component";
import { WorkflowsComponent } from "./pages/automation/workflows/workflows/workflows.component";
import { MyWorkflowsComponent } from "./pages/automation/workflows/my-workflows/my-workflows.component";
import { WorkflowManagerComponent } from "./pages/automation/workflows/workflow-manager/workflow-manager.component";
import {
  InstructionBuilderComponent
} from './pages/automation/instructions/instruction-builder/instruction-builder.component';
import { WorkflowBuilderComponent } from "./pages/automation/workflows/workflow-builder/workflow-builder.component";
import {
  InstructionManagerComponent
} from "./pages/automation/instructions/instruction-manager/instruction-manager.component";
import { MyInstructionsComponent } from "./pages/automation/instructions/my-instructions/my-instructions.component";
import { manageAutomationGuard, myAutomationGuard, useAutomationGuard } from './guards/automation.guard';

export const routes: Routes = [
  {
    path: RoutePath.Root,
    component: UiLayoutComponent,
    canActivate: [ authGuard, apiGuard ],
    children: [
      // Session routes
      {
        path: RoutePath.Root,
        redirectTo: RoutePath.Session,
        pathMatch: 'full'
      },
      {
        path: RoutePath.StartConversation,
        loadComponent: () => import('./pages/conversation-starter/conversation-starter.component').then(m => m.ConversationStarterComponent),
        canActivate: [ MsalGuard ]
      },
      {
        path: RoutePath.Marketplace,
        loadComponent: () => import('./pages/marketplace/marketplace').then(m => m.Marketplace),
        canActivate: [ MsalGuard ]
      },
      {
        path: RoutePath.Session,
        component: AiChatComponent,
        canActivate: [ MsalGuard ]
      },
      {
        path: `${RoutePath.Session}/:sessionId`,
        component: AiChatComponent,
        canActivate: [ MsalGuard ]
      },
      {
        path: RoutePath.ManageSessions,
        component: ManageSessionsComponent,
        canActivate: [ MsalGuard ]
      },
      // Agent routes
      {
        path: RoutePath.CreateAgent,
        component: AgentBuilderComponent,
        canActivate: [ MsalGuard ]
      },
      {
        path: `${RoutePath.EditAgent}/:agentId`,
        component: AgentBuilderComponent,
        canActivate: [ MsalGuard ]
      },
      {
        path: `${RoutePath.EditAgent}/:agentId/:step`,
        component: AgentBuilderComponent,
        canActivate: [ MsalGuard ]
      },
      {
        path: `${RoutePath.CloneAgent}/:agentId`,
        component: AgentBuilderComponent,
        canActivate: [ MsalGuard ]
      },
      {
        path: RoutePath.MyAgents,
        component: AgentListComponent,
        canActivate: [ MsalGuard ]
      },
      {
        path: RoutePath.ManageAgents,
        component: AgentManagerComponent,
        canActivate: [ MsalGuard ]
      },
        // Admin routes
      {
        path: RoutePath.ManageProfiles,
        loadComponent: () => import('./pages/admin/profile-manager/profile-manager.component').then(m => m.ProfileManagerComponent),
        canActivate: [ MsalGuard ]
      },
      {
        path: RoutePath.Gsm,
        component: GsmManagerComponent,
        canActivate: [ MsalGuard ]
      },
      {
        path: RoutePath.ManageKeywords,
        loadComponent: () => import('./pages/admin/keyword-manager/keyword-manager.component').then(m => m.KeywordManagerComponent),
        canActivate: [ MsalGuard ]
      },
        // Automation routes
      {
        path: RoutePath.Workflows,
        component: WorkflowsComponent,
        canActivate: [ MsalGuard, useAutomationGuard ]
      },
      {
        path: `${RoutePath.Workflows}/:workflowId`,
        component: WorkflowsComponent,
        canActivate: [ MsalGuard, useAutomationGuard ]
      },
      {
        path: RoutePath.CreateWorkflow,
        component: WorkflowBuilderComponent,
        canActivate: [ MsalGuard, myAutomationGuard ]
      },
      {
        path: `${RoutePath.EditWorkflow}/:workflowId`,
        component: WorkflowBuilderComponent,
        canActivate: [ MsalGuard, myAutomationGuard ]
      },
      {
        path: `${RoutePath.EditWorkflow}/:workflowId/:step`,
        component: WorkflowBuilderComponent,
        canActivate: [ MsalGuard, myAutomationGuard ]
      },
      {
        path: `${RoutePath.CloneWorkflow}/:workflowId`,
        component: WorkflowBuilderComponent,
        canActivate: [ MsalGuard, myAutomationGuard ]
      },
      {
        path: RoutePath.MyWorkflows,
        component: MyWorkflowsComponent,
        canActivate: [ MsalGuard, myAutomationGuard ]
      },
      {
        path: RoutePath.ManageWorkflows,
        component: WorkflowManagerComponent,
        canActivate: [ MsalGuard, manageAutomationGuard ]
      },
      {
        path: RoutePath.ManageInstructions,
        component: InstructionManagerComponent,
        canActivate: [ MsalGuard, manageAutomationGuard ]
      },
      {
        path: RoutePath.MyInstructions,
        component: MyInstructionsComponent,
        canActivate: [ MsalGuard, myAutomationGuard ]
      },
      {
        path: RoutePath.CreateInstruction,
        component: InstructionBuilderComponent,
        canActivate: [ MsalGuard, myAutomationGuard ]
      },
      {
        path: `${RoutePath.EditInstruction}/:instructionId`,
        component: InstructionBuilderComponent,
        canActivate: [ MsalGuard, myAutomationGuard ]
      },
      {
        path: `${RoutePath.EditInstruction}/:instructionId/:step`,
        component: InstructionBuilderComponent,
        canActivate: [ MsalGuard, myAutomationGuard ]
      },
      {
        path: `${RoutePath.CloneInstruction}/:instructionId`,
        component: InstructionBuilderComponent,
        canActivate: [ MsalGuard, myAutomationGuard ]
      }
    ]
  },
  {
    path: RoutePath.SignIn,
    component: AuthComponent
  },
  {
    path: '**',
    redirectTo: RoutePath.SignIn
  }
];
